import {
  DC_Belgium_Client_Models_RegisteredProductType,
  DC_Repositories_Base_Enumerations_BusinessUnit,
  DC_Repositories_Base_Enumerations_Label,
  Enums_SubscriptionTier,
  Enums_SubscriptionType,
  RequestModels_Subscriptions_RegisterSubscriptionRequestModel,
  ResponseModels_Subscriptions_AvailableProductsInfoResponseModel,
  ResponseModels_Subscriptions_CustomerInfoResponseModel,
  ResponseModels_Subscriptions_LookupSubscriptionsResponseModel,
  ResponseModels_Subscriptions_RegisteredProductResponseModel,
  ResponseModels_Subscriptions_RegisteredProductsListResponseModel,
  ResponseModels_Subscriptions_SubscriptionResponseModel,
  ResponseModels_Subscriptions_ValidateOrderResponseModel,
  Subscriptions_CreateRegisteredProductRequestModel,
  Subscriptions_GetAvailableProductsRequestModel,
  Subscriptions_OrderProductRequestModel,
  Subscriptions_UpdateRegisteredProductStatusRequestModel,
} from '@monorepo-types/dc';

import { request } from '../client';
import type { ApiRequestConfig } from '../client/types';

type LookupSubscriptions = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  tier?: Enums_SubscriptionTier;
  type?: Enums_SubscriptionType;
  includeFutureSubscriptions?: boolean;
};
/**
 * Look up subscriptions
 *
 * @returns ResponseModels_Subscriptions_LookupSubscriptionsResponseModel Success
 */
export function lookupSubscriptions(
  { businessUnit, label, customerId, tier, type, includeFutureSubscriptions }: LookupSubscriptions,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Subscriptions_LookupSubscriptionsResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions`,
      query: { tier, type, includeFutureSubscriptions },
    },
    requestConfig,
  );
}

type RegisterSubscription = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody?: RequestModels_Subscriptions_RegisterSubscriptionRequestModel;
};
/**
 * Register a new subscription that is unique
 *
 * @returns ResponseModels_Subscriptions_SubscriptionResponseModel Success
 */
export function registerSubscription(
  { businessUnit, label, customerId, requestBody }: RegisterSubscription,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Subscriptions_SubscriptionResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions`,
      body: requestBody,
      errors: { 404: 'Not Found' },
    },
    requestConfig,
  );
}

type CancelActiveSubscription = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  subscriptionId: string;
};
/**
 * Cancel an existing active subscription
<param name="label">The label.</param><param name="businessUnit">The businessUnit.</param><param name="customerId">Customer id</param><param name="subscriptionId">The id of the subscription to cancel.</param><param name="cancellationToken">The cancellationToken.</param>
 * 
 * @returns void 
 */
export function cancelActiveSubscription(
  { businessUnit, label, customerId, subscriptionId }: CancelActiveSubscription,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'DELETE',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions/${subscriptionId}`,
      errors: { 404: 'Not Found', 422: 'Client Error' },
    },
    requestConfig,
  );
}

type GetCustomerInfo = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
};
/**
 * Get customer info
 *
 * @returns ResponseModels_Subscriptions_CustomerInfoResponseModel Success
 */
export function getCustomerInfo(
  { businessUnit, label, customerId }: GetCustomerInfo,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Subscriptions_CustomerInfoResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions/customer/info`,
      errors: { 400: 'Bad Request', 404: 'Not Found' },
    },
    requestConfig,
  );
}

type GetAvailableProducts = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: Subscriptions_GetAvailableProductsRequestModel;
};
/**
 * Get available products
 *
 * @returns ResponseModels_Subscriptions_AvailableProductsInfoResponseModel Success
 */
export function getAvailableProducts(
  { businessUnit, label, customerId, requestBody }: GetAvailableProducts,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Subscriptions_AvailableProductsInfoResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions/products`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type ValidateOrder = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: Subscriptions_OrderProductRequestModel;
  prepareCheckout?: boolean;
};
/**
 * Validate order
 *
 * @returns ResponseModels_Subscriptions_ValidateOrderResponseModel Success
 */
export function validateOrder(
  { businessUnit, label, customerId, requestBody, prepareCheckout }: ValidateOrder,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Subscriptions_ValidateOrderResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions/products/validate`,
      query: { prepareCheckout },
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type RegisterProduct = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody: Subscriptions_CreateRegisteredProductRequestModel;
};
/**
 * Register a purchased product
 *
 * @returns ResponseModels_Subscriptions_RegisteredProductResponseModel Success
 */
export function registerProduct(
  { businessUnit, label, customerId, requestBody }: RegisterProduct,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Subscriptions_RegisteredProductResponseModel> {
  return request(
    {
      method: 'POST',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions/products/register`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type GetRegisteredProducts = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  type?: DC_Belgium_Client_Models_RegisteredProductType;
};
/**
 * Get Registered products back for a specific customer
 *
 * @returns ResponseModels_Subscriptions_RegisteredProductsListResponseModel Success
 */
export function getRegisteredProducts(
  { businessUnit, label, customerId, type }: GetRegisteredProducts,
  requestConfig: ApiRequestConfig = {},
): Promise<ResponseModels_Subscriptions_RegisteredProductsListResponseModel> {
  return request(
    {
      method: 'GET',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions/products/registered`,
      query: { type },
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type UpdateRegisteredProductStatus = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
  requestBody?: Subscriptions_UpdateRegisteredProductStatusRequestModel;
};
/**
 * Endpoint for updating the purchase status of a registered product
 *
 * @returns void
 */
export function updateRegisteredProductStatus(
  { businessUnit, label, customerId, requestBody }: UpdateRegisteredProductStatus,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'PATCH',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions/products/status`,
      body: requestBody,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}

type DeleteCustomerCache = {
  businessUnit: DC_Repositories_Base_Enumerations_BusinessUnit;
  label: DC_Repositories_Base_Enumerations_Label;
  customerId: number;
};
/**
 * Endpoint for deleting customer cache
 *
 * @returns void
 */
export function deleteCustomerCache(
  { businessUnit, label, customerId }: DeleteCustomerCache,
  requestConfig: ApiRequestConfig = {},
): Promise<void> {
  return request(
    {
      method: 'DELETE',
      path: `/dxpweb/${businessUnit}/${label}/customers/${customerId}/subscriptions/products/cache`,
      errors: { 400: 'Bad Request' },
    },
    requestConfig,
  );
}
